/**
 * Idle-timeout warning toast.
 *
 * Listens for the `idle:warning` event emitted by `lib/idle-timer.ts`
 * and renders a fixed-bottom amber toast with a live countdown. Any
 * tap on "I'm here" dispatches a synthetic activity event which the
 * idle-timer picks up and resets. If the timer expires, the user is
 * signed out automatically — at that point the LoginPage replaces
 * the shell so this toast disappears with it.
 */

import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

export function IdleWarningToast() {
  const [msLeft, setMsLeft] = useState<number | null>(null);

  useEffect(() => {
    const onWarn = (e: Event) => {
      const detail = (e as CustomEvent<{ msUntilLogout: number }>).detail;
      setMsLeft(detail.msUntilLogout);
    };
    const onClear = () => setMsLeft(null);

    window.addEventListener('idle:warning', onWarn as EventListener);
    window.addEventListener('idle:cleared', onClear);
    window.addEventListener('idle:logout', onClear);
    return () => {
      window.removeEventListener('idle:warning', onWarn as EventListener);
      window.removeEventListener('idle:cleared', onClear);
      window.removeEventListener('idle:logout', onClear);
    };
  }, []);

  // Tick the displayed countdown every second while the warning is active.
  useEffect(() => {
    if (msLeft == null) return;
    const id = setInterval(() => {
      setMsLeft((prev) => (prev == null ? null : Math.max(0, prev - 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [msLeft]);

  if (msLeft == null) return null;

  const mins = Math.floor(msLeft / 60_000);
  const secs = Math.floor((msLeft % 60_000) / 1000);

  const stayActive = () => {
    // A real input event is the canonical reset signal — synthesize
    // one rather than reaching across module boundaries.
    window.dispatchEvent(new Event('keydown'));
  };

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)] rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 shadow-lg flex items-center gap-3">
      <Clock size={20} className="text-amber-700 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-amber-900">
          Signing you out for inactivity
        </div>
        <div className="text-xs text-amber-800">
          Logout in {mins}:{String(secs).padStart(2, '0')} — tap below to stay signed in.
        </div>
      </div>
      <button
        onClick={stayActive}
        className="shrink-0 rounded-md border border-amber-700 bg-amber-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-800"
      >
        I'm here
      </button>
    </div>
  );
}
